import type { JSX } from 'react';

import { RefreshCw, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

import type { ActivityStatus } from '../../model/activity';
import type { StatusFilterOption } from '../../model/activity-display';

interface ActivityToolbarProps {
  searchRaw: string;
  status: ActivityStatus;
  statusFilters: StatusFilterOption[];
  isFetching: boolean;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onStatusChange: (status: ActivityStatus) => void;
  onRefresh: () => void;
}

const ActivityToolbar = ({
  searchRaw,
  status,
  statusFilters,
  isFetching,
  onSearchChange,
  onClearSearch,
  onStatusChange,
  onRefresh,
}: ActivityToolbarProps): JSX.Element => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="relative min-w-[220px] flex-1">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-muted"
        aria-hidden
      />
      <Input
        type="search"
        value={searchRaw}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Buscar por cliente, proyecto o contenido…"
        aria-label="Buscar actividad"
        className="pl-8 pr-8"
      />
      {searchRaw ? (
        <button
          type="button"
          onClick={onClearSearch}
          aria-label="Limpiar búsqueda"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-muted hover:text-text-default"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      ) : null}
    </div>

    <Select value={status} onValueChange={(value) => onStatusChange(value as ActivityStatus)}>
      <SelectTrigger size="default" aria-label="Filtrar por estado" className="min-w-[180px]">
        <SelectValue placeholder="Estado" />
      </SelectTrigger>
      <SelectContent position="popper">
        {statusFilters.map((filter) => (
          <SelectItem key={filter.value} value={filter.value}>
            {filter.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>

    <Button
      variant="ghost"
      size="sm"
      className="gap-1.5 text-xs text-text-muted"
      onClick={onRefresh}
      disabled={isFetching}
      aria-label="Actualizar actividad"
    >
      <RefreshCw className={cn('size-3.5', isFetching && 'animate-spin')} aria-hidden />
      Actualizar
    </Button>
  </div>
);

export default ActivityToolbar;
